/**
 * The scope pickers of Price limits — lifted out of the old inline table
 * (CU-869fkbjdh § 3.1). They used to live in every row; now they live in
 * the drawer's "Where" card and in the filter strip. Behaviour unchanged;
 * colours only from `dsColorNew`.
 */
import React, { useMemo } from 'react'
import { Autocomplete, Box, Chip, MenuItem, Select, TextField, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { dsColorNew } from '@palette'
import { useCategories } from 'Pages/DsRepricing/Setup/@services/useCategories'
import { useOfficeScope } from 'Pages/DsRepricing/Setup/@services/useOfficeScope'
import { inputSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import DurationRangeField from 'Pages/DsRepricing/@components/DurationRangeField'

/**
 * An enum axis as a select whose value reads as a chip; tolerates a
 * value not present in `options` (imported from CSV) by rendering it as
 * a custom chip + a dynamic MenuItem, so it stays selectable instead of
 * falling out of the model on next open.
 */
export function EnumScopeSelect({ value, options, onChange, variant, allLabel, sx }) {
	const palette = {
		site:      { bg: dsColorNew.pillActiveBg, fg: dsColorNew.appBlue, border: dsColorNew.noteInfoBorder },
		tariff:    { bg: dsColorNew.noteInfoBg, fg: dsColorNew.noteInfo, border: dsColorNew.noteInfoBorder },
		insurance: { bg: dsColorNew.noteAmberBg, fg: dsColorNew.noteAmber, border: dsColorNew.noteAmberBorder },
		franchise: { bg: dsColorNew.pillTrackBg, fg: dsColorNew.textDark, border: dsColorNew.grayBorder },
	}[variant] || { bg: dsColorNew.pillTrackBg, fg: dsColorNew.textDark, border: dsColorNew.grayBorder }
	const isCustom = value && !options.some(o => o.code === value)
	return (
		<Select size="small" fullWidth displayEmpty
		        value={value ?? ''}
		        onChange={(e) => onChange(e.target.value || null)}
		        renderValue={(v) => {
		          if (!v) return (
		            <Chip size="small"
		                  label={allLabel}
		                  sx={{ height: 22, fontWeight: 700, fontSize: 11,
		                        letterSpacing: '0.02em',
		                        maxWidth: '100%',
		                        bgcolor: dsColorNew.white,
		                        color: dsColorNew.textMuted,
		                        border: '1px dashed', borderColor: dsColorNew.textSubtle,
		                        fontStyle: 'italic',
		                        '& .MuiChip-label': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}/>
		          )
		          const opt = options.find(o => o.code === v)
		          return (
		            <Chip size="small"
		                  label={opt?.name || v}
		                  sx={{ height: 22, fontWeight: 700, fontSize: 11,
		                        maxWidth: '100%',
		                        bgcolor: palette.bg, color: palette.fg,
		                        border: '1px solid', borderColor: palette.border,
		                        '& .MuiChip-label': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}/>
		          )
		        }}
		        sx={{ ...inputSx, ...sx }}>
			<MenuItem value="">
				<em style={{ color: dsColorNew.textMuted }}>{allLabel}</em>
			</MenuItem>
			{options.map(o => (
				<MenuItem key={o.code} value={o.code}>{o.name}</MenuItem>
			))}
			{isCustom && (
				<MenuItem value={value}>
					<Box component="code" sx={{ mr: 1, fontWeight: 700, fontFamily: 'inherit' }}>{value}</Box>
					<em style={{ color: dsColorNew.textMuted }}>(imported)</em>
				</MenuItem>
			)}
		</Select>
	)
}

/**
 * Any office, a group, or one or more offices — typed, not hunted for.
 *
 * It was a `Select`, which offers no way to search: a tenant with a
 * hundred locations meant scrolling a menu to find `TFS-AP` (Curro,
 * 2026-09-29). An Autocomplete over the same two groups takes a few
 * keystrokes instead, and matches the code as well as the name —
 * people know the code. Not the `@components/OfficeScopePicker` of the
 * wizard and Schedule: that one has another contract.
 */
export function OfficeScopePicker({ rule, onChange, placeholder = 'Any office — or pick offices / a group…' }) {
	const { offices, groups } = useOfficeScope()
	const chosen = rule.scope_offices || []

	const options = useMemo(() => {
		const out = []
		for (const g of groups) {
			out.push({ key: `group:${g.code}`, label: g.name, code: g.code, group: 'Office groups' })
		}
		for (const o of offices) {
			out.push({ key: `office:${o.code}`, label: o.name || o.code, code: o.code, group: 'Offices' })
		}
		// Codes from a CSV import the catalogue does not know. Dropping
		// them would silently rescope the rule on open.
		for (const code of chosen) {
			if (!offices.some(o => o.code === code)) {
				out.push({ key: `office:${code}`, code, label: 'imported', group: 'Offices' })
			}
		}
		return out
	}, [offices, groups, chosen.join('|')])

	// One group, or any number of offices, or nothing — never both. A
	// group names a set somebody else maintains; offices spell it out.
	const value = rule.scope_office_group
		? options.filter(o => o.key === `group:${rule.scope_office_group}`)
		: options.filter(o => o.key.startsWith('office:') && chosen.includes(o.code))

	return (
		<Autocomplete size="small" fullWidth multiple disableCloseOnSelect
		              options={options}
		              value={value}
		              groupBy={(o) => o.group}
		              isOptionEqualToValue={(a, b) => a.key === b.key}
		              getOptionLabel={(o) => (o.code ? `${o.code} · ${o.label}` : o.label)}
		              filterOptions={(opts, { inputValue }) => {
		                  const q = inputValue.trim().toLowerCase()
		                  if (!q) return opts
		                  return opts.filter(o => `${o.code || ''} ${o.label}`.toLowerCase().includes(q))
		              }}
		              onChange={(_, picked) => {
		                  const last = picked[picked.length - 1]
		                  if (!last) return onChange({ scope_offices: null, scope_office_group: null })
		                  // The newest pick decides the kind; the other kind is dropped.
		                  if (last.key.startsWith('group:')) {
		                      return onChange({ scope_offices: null, scope_office_group: last.code })
		                  }
		                  const codes = picked.filter(o => o.key.startsWith('office:')).map(o => o.code)
		                  return onChange({ scope_offices: codes.length ? codes : null, scope_office_group: null })
		              }}
		              renderTags={(sel, getTagProps) => sel.map((o, i) => (
		                  <Chip {...getTagProps({ index: i })} key={o.key} size="small"
		                        label={o.key.startsWith('group:') ? `${o.label} (group)` : o.code}
		                        sx={{ fontWeight: 700 }}/>
		              ))}
		              renderOption={(props, o) => (
		                  <li {...props} key={o.key}>
		                      {o.code && (
		                          <Box component="code" sx={{ mr: 1, fontWeight: 700, fontFamily: 'inherit' }}>
		                              {o.code}
		                          </Box>
		                      )}
		                      <Typography sx={{ fontSize: 13 }}>{o.label}</Typography>
		                  </li>
		              )}
		              renderInput={(params) => (
		                  <TextField {...params} sx={inputSx}
		                             placeholder={value.length ? '' : placeholder}/>
		              )}/>
	)
}

/** Macros and SIPPs of the tenant's own catalogue; `disabled` when a matrix sets them. */
export function CategoryScopePicker({ value, onChange, disabled = false, placeholder = 'Any category' }) {
	const { tree } = useCategories()
	const options = useMemo(() => {
		const opts = []
		for (const m of tree) {
			opts.push({ type: 'macro', code: m.code, label: `All ${m.name}`, group: m.name })
			for (const s of m.sipps) {
				opts.push({ type: 'sipp', code: s, label: s, group: m.name })
			}
		}
		return opts
	}, [tree])
	const optionsWithCustom = useMemo(() => {
		if (!value?.length) return options
		const known = new Set(options.map(o => `${o.type}:${o.code}`))
		const extra = value
			.filter(v => !known.has(`${v.type}:${v.code}`))
			.map(v => ({ type: v.type, code: v.code, label: v.code, group: '(imported)' }))
		return [...options, ...extra]
	}, [options, value])
	const selected = useMemo(() => {
		if (!value?.length) return []
		return optionsWithCustom.filter(o => value.some(v => v.type === o.type && v.code === o.code))
	}, [value, optionsWithCustom])
	return (
		<Autocomplete multiple size="small" disabled={disabled}
		              options={optionsWithCustom}
		              value={selected}
		              disableCloseOnSelect
		              groupBy={(o) => o.group}
		              getOptionLabel={(o) => o.label}
		              isOptionEqualToValue={(a, b) => a.type === b.type && a.code === b.code}
		              onChange={(_, next) =>
		                onChange(next.length
		                  ? next.map(o => ({ type: o.type, code: o.code }))
		                  : null)}
		              renderTags={(v, getTagProps) =>
		                v.map((o, idx) => (
		                  <Chip {...getTagProps({ index: idx })}
		                        key={`${o.type}:${o.code}`} size="small"
		                        label={o.type === 'macro' ? o.label : o.code}
		                        sx={{
		                            fontWeight: 700, fontSize: 11,
		                            bgcolor: o.type === 'macro' ? dsColorNew.pillActiveBg : dsColorNew.noteInfoBg,
		                            color: dsColorNew.appBlue,
		                            border: '1px solid',
		                            borderColor: o.type === 'macro' ? alpha(dsColorNew.appBlue, 0.45) : dsColorNew.noteInfoBorder,
		                        }}/>
		                ))}
		              renderOption={(props, o, { selected: sel }) => (
		                <li {...props} key={`${o.type}:${o.code}`}
		                    style={{
		                      ...props.style,
		                      paddingLeft: o.type === 'macro' ? 16 : 32,
		                      fontWeight: o.type === 'macro' ? 700 : 500,
		                      background: sel ? dsColorNew.hoverBgLight : undefined,
		                    }}>
		                  {o.type === 'macro'
		                    ? <span style={{ color: dsColorNew.appBlue }}>{o.label}</span>
		                    : o.code}
		                </li>
		              )}
		              renderGroup={(params) => (
		                <li key={params.key}>
		                  <Box sx={{
		                      px: 1.5, py: 0.5,
		                      bgcolor: dsColorNew.pillTrackBg,
		                      fontSize: 11, fontWeight: 700,
		                      textTransform: 'uppercase', letterSpacing: '0.06em',
		                      color: dsColorNew.textMuted,
		                      position: 'sticky', top: -8, zIndex: 1,
		                  }}>
		                    {params.group}
		                  </Box>
		                  <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
		                </li>
		              )}
		              renderInput={(params) => (
		                <TextField {...params}
		                           placeholder={selected.length ? '' : (disabled ? 'set by the matrix' : placeholder)}
		                           sx={inputSx}/>
		              )}/>
	)
}

/**
 * Min and max, in days, open-ended — the shared control (CU-869fhtp3a).
 * No unit selector: there used to be one — days, weeks, months — and the
 * engine never read it, so "2 to 4 weeks" matched 2 to 4 **day** rentals
 * (Curro, 2026-09-30). Everything is days. `disabled` when a matrix's
 * bands set the length.
 */
export function DurationScopePicker({ value, onChange, disabled = false }) {
	if (disabled) {
		return (
			<Typography sx={{ fontSize: 13, color: dsColorNew.textSubtle, fontStyle: 'italic', py: 1 }}>
				set by the matrix bands
			</Typography>
		)
	}
	return (
		<DurationRangeField value={value} openEnd
		                    onChange={(v) => onChange(v && { unit: 'days', ...v })}/>
	)
}

/** A small select of the filter strip. */
export function CompactSelect({ value, onChange, options, minWidth = 150, sx = {} }) {
	return (
		<Select size="small" value={value}
		        onChange={(e) => onChange(e.target.value)}
		        sx={{ minWidth, ...sx }}>
			{options.map(o => (
				<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
			))}
		</Select>
	)
}
